import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import SalesAchievementListTable from '../components/SalesAchievementListTable';
import { fetchIncentiveClaimData } from '../api/salesReportApi';

const SalesAchievement = ({
    salesData,
    metrics,
    isLoading,
    searchQuery,
    setSearchQuery,
    filters,
    handleExportExcel,
    formatCurrencyValue
}) => {
    const navigate = useNavigate();

    const generateIncentivePdf = async () => {
        // Fetch all invoice-level incentive data from the dedicated backend endpoint.
        // The backend resolves productName from InvoiceItems and calculates all incentive fields.
        let backendData;
        try {
            const creatorIds = Array.isArray(filters?.creator) && filters.creator.length > 0
                ? filters.creator
                : [];
            backendData = await fetchIncentiveClaimData({
                period: filters?.period,
                startDate: filters?.startDate,
                endDate: filters?.endDate,
                creatorIds,
                branchId: filters?.branchId,
                taggingIds: filters?.taggingIds
            });
        } catch (err) {
            console.error('Failed to fetch incentive claim data', err);
            alert('Failed to load incentive data from server. Please try again.');
            return;
        }

        const items = backendData?.items || [];
        const creatorIds = Array.isArray(filters?.creator) ? filters.creator : [];
        const selectedCreatorId = creatorIds.length === 1 ? String(creatorIds[0]) : null;
        const selectedSalesRow = selectedCreatorId
            ? salesData.find(
                (row) =>
                    row?.name !== 'GRAND TOTAL' &&
                    row?.id != null &&
                    String(row.id) === selectedCreatorId
            )
            : null;
        const backendSalesName = (backendData?.salesName || '').toString().trim();
        const salesName = selectedSalesRow?.name
            || (backendSalesName && backendSalesName.toLowerCase() !== 'all' ? backendSalesName : '')
            || 'All';

        let achievementText = '-';
        if (selectedSalesRow) {
            const targetForPeriod = Number(selectedSalesRow.targetForPeriod || 0);
            if (targetForPeriod > 0) {
                achievementText = `${Number(Number(selectedSalesRow.achievementOfTarget || 0).toFixed(1))}%`;
            }
        } else if (metrics?.achievement != null) {
            achievementText = `${Number(Number(metrics.achievement).toFixed(1))}%`;
        }

        const getPeriodDisplay = () => {
            const period = filters?.period;
            const selectedYear = filters?.year;
            if (selectedYear) return String(selectedYear);

            const now = new Date();
            const currentYear = now.getFullYear();

            if (period === 'this_year') return String(currentYear);
            if (period === 'last_year') return String(currentYear - 1);

            if (period === 'custom' && filters?.startDate && filters?.endDate) {
                const startYear = new Date(filters.startDate).getFullYear();
                const endYear = new Date(filters.endDate).getFullYear();
                return startYear === endYear ? String(startYear) : `${startYear}-${endYear}`;
            }

            // For month/quarter presets, display the current year context.
            if (period === 'this_month' || period === 'last_month' || period === 'this_quarter' || period === 'last_quarter') {
                return String(currentYear);
            }

            return String(currentYear);
        };
        const periodDisplay = getPeriodDisplay();

        const rows = items.map((item, idx) => ({
            no: idx + 1,
            month: item.month || '',
            invoiceNumber: item.invoiceNumber || '-',
            clientName: item.clientName || '-',
            productTagging: (() => {
                const normalizedTagging = (item.productTagging || '').toString().trim().toLowerCase();
                if (!normalizedTagging || normalizedTagging === 'non push product') {
                    return '-';
                }
                if (normalizedTagging === 'push product') {
                    return 'Push Product';
                }
                return item.productTagging;
            })(),
            monthIdr: Number(item.monthAmountIdr || 0),
            pushProduct: Number(item.pushProductIncentiveIdr || 0),
            totalIncentive: Number(item.totalIncentiveIdr || 0)
        }));

        const totalIncentiveSum = Number(backendData?.grandTotalIncentive || backendData?.incentive || 0);

        // Create printable element with fixed table layout and sensible column widths
        const style = `
            body { font-family: Arial, sans-serif; padding: 10px; color: #222; }
            h1 { text-align: center; font-size: 18px; margin-bottom: 8px; }
            .meta { margin-bottom: 8px; font-size: 13px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; table-layout: fixed; box-sizing: border-box; }
            thead th { background: #0bb04b; color: white; padding: 8px; text-align: left; vertical-align: middle; box-sizing: border-box; }
            tbody td { padding: 8px; border-bottom: 1px solid #eee; vertical-align: top; box-sizing: border-box; }
            tfoot td { padding: 8px; font-weight: bold; box-sizing: border-box; }
            .right { text-align: right; white-space: nowrap; }
            th, td { overflow: hidden; text-overflow: ellipsis; }
            /* Compact numeric columns */
            .col-no, .col-month, .col-invoice, .col-value { white-space: nowrap; }
            .col-client {
                white-space: normal;
                overflow: visible;
                text-overflow: clip;
                word-break: break-word;
                overflow-wrap: anywhere;
            }
        `;

        const tableRows = rows.map(r => `
            <tr>
                <td class="col-no">${r.no}</td>
                <td class="col-month">${r.month || ''}</td>
                <td class="col-invoice">${r.invoiceNumber}</td>
                <td class="col-client">${r.clientName}</td>
                <td class="col-tag">${r.productTagging}</td>
                <td class="col-value right">Rp ${formatCurrencyValue(r.monthIdr || 0)}</td>
                <td class="col-value right">Rp ${formatCurrencyValue(r.pushProduct || 0)}</td>
                <td class="col-value right">Rp ${formatCurrencyValue(r.totalIncentive || 0)}</td>
            </tr>
        `).join('');

        const container = document.createElement('div');
        container.style.display = 'block';
        container.style.padding = '8px';
        container.innerHTML = `
            <div>
                <style>${style}</style>
                <h1>INCENTIVE CLAIM CALCULATION FORM</h1>
                <div class="meta">
                    <div>Sales Name: ${salesName}</div>
                    <div>Period: ${periodDisplay}</div>
                    <div>Achivement (%) = ${achievementText}</div>
                </div>
                <table>
                    <colgroup>
                        <col style="width:3%" />
                        <col style="width:6%" />
                        <col style="width:12%" />
                        <col style="width:20%" />
                        <col style="width:12%" />
                        <col style="width:16%" />
                        <col style="width:16%" />
                        <col style="width:15%" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>No</th>
                            <th>Month</th>
                            <th>Invoice Number</th>
                            <th>Client Name</th>
                            <th>Product Tagging</th>
                            <th class="right">Month (IDR)</th>
                            <th class="right">Push Product (IDR)</th>
                            <th class="right">Total Incentive (IDR)</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colspan="7" class="right">Incentive</td>
                            <td class="right">Rp ${formatCurrencyValue(totalIncentiveSum)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        `;

        document.body.appendChild(container);

        // Load html2pdf (bundle with html2canvas + jsPDF) via CDN if not present
        const loadHtml2Pdf = () => {
            return new Promise((resolve, reject) => {
                if (window.html2pdf) return resolve(window.html2pdf);
                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.9.3/html2pdf.bundle.min.js';
                script.onload = () => resolve(window.html2pdf);
                script.onerror = (e) => reject(e);
                document.head.appendChild(script);
            });
        };

        try {
            const html2pdf = await loadHtml2Pdf();
            const opt = {
                margin:       0.2,
                filename:     'incentive-claim.pdf',
                image:        { type: 'jpeg', quality: 0.98 },
                html2canvas:  { scale: 2, useCORS: true },
                jsPDF:        { unit: 'in', format: 'a4', orientation: 'landscape' }
            };
            await html2pdf().set(opt).from(container).save();
        } catch (err) {
            console.error('PDF generation failed', err);
            alert('Failed to generate PDF. Please allow popups or try again.');
        } finally {
            // clean up DOM
            if (container && container.parentNode) container.parentNode.removeChild(container);
        }
    };

    const incentiveButton = (
        <button
            type="button"
            className="btn btn-success"
            onClick={generateIncentivePdf}
        >
            <Inbox className="icon icon-sm me-1" size={18} />
            Incentive Claim
        </button>
    );

    const navigateToInvoice = (salespersonData = null, status = null) => {
        // Use the exact bill-date range the backend resolved for this period/metrics,
        // so the invoice list matches the counts shown on the cards/table.
        const billDateFrom = metrics?.billDateFrom || filters.startDate;
        const billDateTo = metrics?.billDateTo || filters.endDate;

        const params = new URLSearchParams();
        if (billDateFrom) params.append('billDateFrom', billDateFrom);
        if (billDateTo) params.append('billDateTo', billDateTo);

        if (salespersonData && salespersonData.id && salespersonData.name !== 'GRAND TOTAL') {
            params.append('assignedTo', salespersonData.id);
        } else if (Array.isArray(filters?.creator) && filters.creator.length > 0) {
            // No specific row clicked — carry over the active Sales Creator filter.
            params.append('assignedTo', filters.creator[0]);
        }

        const serviceIds = Array.isArray(filters.service) ? filters.service : (filters.service ? [filters.service] : []);
        if (serviceIds.length > 0 && serviceIds[0] != null && serviceIds[0] !== '') {
            params.append('serviceId', String(serviceIds[0]));
        }

        const departmentIds = Array.isArray(filters.departmentId) ? filters.departmentId : (filters.departmentId ? [filters.departmentId] : []);
        if (departmentIds.length > 0 && departmentIds[0] != null && departmentIds[0] !== '') {
            params.append('departmentId', String(departmentIds[0]));
        }

        if (status) {
            if (Array.isArray(status)) {
                status.forEach(s => params.append('status', s));
            } else {
                params.append('status', status);
            }
        } else {
            params.append('status', 'SENT');
            params.append('status', 'PAID');
            params.append('status', 'FINAL');
            params.append('status', 'PARTIAL');
        }

        navigate(`/invoice?${params.toString()}`);
    };

    const totalInvoiceCount = metrics.invoiceCount || 0;

    // Get actual paid and outstanding invoice counts from backend
    const paidInvoiceCount = metrics.paidInvoiceCount || 0;
    const outstandingInvoiceCount = metrics.outstandingInvoiceCount || 0;

    const targetRevenue = metrics.target || 0;

    return (
        <>
            {/* Metric Cards */}
            <h3 className="mb-3">Achievement per Salesperson (Assigned Sales Owner)</h3>
            <div className="row mb-4">
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-cyan"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">Total Invoice</div>
                                <div className="h3 mb-0">Rp {formatCurrencyValue(metrics.totalRevenue)}</div>
                                <div 
                                    className="h3 text-cyan" 
                                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                                    onClick={() => navigateToInvoice()}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    title="Click to view invoices"
                                >
                                    {totalInvoiceCount} Invoice
                                </div>
                            </div>
                            <div className="text-muted small">Total value of all invoices issued during this period.</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-success"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">Achievement</div>
                                <div className="h3">{metrics.achievement.toFixed(1)}%</div>
                            </div>
                            <div className="text-muted small">
                                Revenue achievement against
                                {metrics.targetPeriodLabel ? ` ${metrics.targetPeriodLabel}` : metrics.targetYear ? ` (${metrics.targetYear})` : ''}
                                {targetRevenue > 0 ? ` — Target: Rp ${formatCurrencyValue(targetRevenue)}` : ' — No target set'}.
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-yellow"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">Paid Invoices</div>
                                <div className="h3 mb-0">Rp {formatCurrencyValue(metrics.invoicesPaid)}</div>
                                <div
                                    className="h3 text-yellow"
                                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                                    onClick={() => navigateToInvoice(null, 'PAID')}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    title="Click to view paid invoices"
                                >
                                    {paidInvoiceCount} Invoice
                                </div>
                            </div>
                            <div className="text-muted small">Total amount received (cash in) from invoices in this period.</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-danger"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">Outstanding Accounts Receivable (AR)</div>
                                <div className="h3 mb-0">Rp {formatCurrencyValue(metrics.outstandingAR)}</div>
                                <div
                                    className="h3 text-danger"
                                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                                    onClick={() => navigateToInvoice(null, ['SENT', 'FINAL', 'PARTIAL'])}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    title="Click to view outstanding invoices"
                                >
                                    {outstandingInvoiceCount} Invoice
                                </div>
                            </div>
                            <div className="text-muted small">Total outstanding balance yet to be collected (Outstanding AR).</div>
                        </div>
                    </div>
                </div>
            </div>

            <SalesAchievementListTable
                salesData={salesData}
                metrics={metrics}
                filters={filters}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                isLoading={isLoading}
                handleExportExcel={handleExportExcel}
                formatCurrencyValue={formatCurrencyValue}
                uniqueId="sales-achievement-list"
                incentiveButton={incentiveButton}
            />
        </>
    );
};

export default SalesAchievement;
